import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useRouter } from 'next/router';
import {
  getPriceByQuery,
  getQueryByPrice,
} from '../../../common/helpers/domain/priceOptions';
import {
  getSrpRouteUrl,
  SrpQueryParams,
} from '../../../common/helpers/routing/srpRoutesHelper';
import { isDesktop } from '../../../common/helpers/utility/screenHelper';
import { getDeviceType } from '../../../store/common/selector';
import { getProjectsTotalCount } from '../../../store/search/selector';
import { WholeState as SearchState } from '../../../store/search/type';
import DesktopFilter from './DesktopFilter';
import MobileFilterModal from './MobileFilterModal';
import SearchFilterHeader from './SearchFilterHeader';
import { PropertyType } from '../../../common/helpers/domain/propertyTypes';

interface SearchFilterProps {
  filterParams: SrpQueryParams;
  title: string;
  srpBlurb: string;
}

const SearchFilter: React.FunctionComponent<SearchFilterProps> = ({
  filterParams,
  title,
  srpBlurb,
}) => {
  const {
    minPrice,
    maxPrice,
    setMaxPrice,
    setMinPrice,
    propertyType,
    setPropertyType,
  } = useFilterCondition(filterParams);
  const projectCount = useSelector(getProjectsTotalCount);
  const deviceType = useSelector(getDeviceType);
  const [showModal, setShowModal] = useState(false);
  const isLoadingProjects = useSelector<SearchState, boolean>((state) =>
    state.search ? state.search.loadingProjectsCarousel : false,
  );
  const router = useRouter();

  useEffect(() => {
    document.querySelector('html').style.overflow = showModal ? 'hidden' : '';
  }, [showModal]);

  const cancelSelection = () => {
    setMinPrice(undefined);
    setMaxPrice(undefined);
    setPropertyType(undefined);
    advanceSearch(undefined, undefined, undefined);
  };

  const advanceSearch = (
    minPrice: number,
    maxPrice: number,
    propertyType: PropertyType,
  ) => {
    const minPriceParam = getQueryByPrice(minPrice);
    const maxPriceParam = getQueryByPrice(maxPrice);
    const url = getSrpRouteUrl({
      type: filterParams.type,
      slug: filterParams.slug,
      parent: filterParams.parent,
      propertyType,
      minPrice: minPriceParam,
      maxPrice: maxPriceParam,
      q: filterParams.q,
    });
    router.push(url).then(() => window.scrollTo(0, 0));
  };

  return (
    <>
      <SearchFilterHeader
        projectCount={projectCount}
        title={title}
        srpBlurb={srpBlurb}
        onClickFilterButton={() => setShowModal(true)}
      />
      {isDesktop(deviceType) && (
        <DesktopFilter
          minPrice={minPrice}
          maxPrice={maxPrice}
          propertyType={propertyType}
          onMinPriceChange={(minPrice) => {
            setMinPrice(minPrice);
            advanceSearch(minPrice, maxPrice, propertyType);
          }}
          onMaxPriceChange={(maxPrice) => {
            setMaxPrice(maxPrice);
            advanceSearch(minPrice, maxPrice, propertyType);
          }}
          onPropertyTypeChange={(propertyType) => {
            setPropertyType(propertyType);
            advanceSearch(minPrice, maxPrice, propertyType);
          }}
          onCancelSelection={cancelSelection}
          isDisabled={isLoadingProjects}
        />
      )}
      {!isDesktop(deviceType) && (
        <MobileFilterModal
          minPrice={minPrice}
          maxPrice={maxPrice}
          propertyType={propertyType}
          closeModal={() => setShowModal(false)}
          isModalOpen={showModal}
          onConfirmSelection={(minPrice, maxPrice, propertyType) => {
            setMinPrice(minPrice);
            setMaxPrice(maxPrice);
            setPropertyType(propertyType);
            setShowModal(false);
            advanceSearch(minPrice, maxPrice, propertyType);
          }}
        />
      )}
    </>
  );
};

function useFilterCondition(query: SrpQueryParams) {
  const [minPrice, setMinPrice] = useState(0);
  const [maxPrice, setMaxPrice] = useState(0);
  const [propertyType, setPropertyType] = useState<PropertyType>(null);

  useEffect(() => {
    setMinPrice(getPriceByQuery(query.minPrice));
    setMaxPrice(getPriceByQuery(query.maxPrice));
    setPropertyType(query.propertyType);
  }, [query.minPrice, query.maxPrice, query.propertyType]);

  return {
    minPrice,
    maxPrice,
    propertyType,
    setMinPrice,
    setMaxPrice,
    setPropertyType,
  };
}

export default SearchFilter;
